/**
 * Color Group Swatches - Section Rendering API
 * Fetches products with matching product_color_group metafield
 * via the Section Rendering API (no authentication needed).
 * 
 * Uses /collections/{handle}?sections=color-group-data to get paginated
 * product data with color group metafield values, then filters client-side
 * for the matching group.
 */
(function () {
  'use strict';

  /** Cache: once we fetch all color group data, reuse it for all swatches on the page */
  let _cachedProducts = null;
  let _fetchPromise = null;

  /**
   * Fetch all products with color group data from the collection,
   * handling pagination (250 per page) via the Section Rendering API.
   */
  function fetchAllColorGroupProducts(collectionHandle) {
    if (_fetchPromise) return _fetchPromise;

    _fetchPromise = (async function () {
      let allProducts = [];
      let page = 1;
      let totalPages = 1;

      while (page <= totalPages) {
        const url = '/collections/' + encodeURIComponent(collectionHandle) + '?sections=color-group-data&page=' + page;
        
        const response = await fetch(url);

        if (!response.ok) {
          console.error('Color group swatches: Section fetch failed', response.status);
          break;
        }

        const responseText = await response.text();
        
        let sectionsData;
        try {
          sectionsData = JSON.parse(responseText);
        } catch (e) {
          console.error('Color group swatches: Response is not JSON');
          break;
        }

        const html = sectionsData['color-group-data'];

        if (!html) {
          console.error('Color group swatches: Section "color-group-data" not found in response');
          break;
        }

        // Parse the JSON from the section HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const scriptEl = doc.querySelector('[data-color-group-products]');

        if (!scriptEl) {
          console.error('Color group swatches: No data element found in section HTML');
          break;
        }

        let data;
        try {
          data = JSON.parse(scriptEl.textContent);
        } catch (e) {
          console.error('Color group swatches: Failed to parse product data JSON');
          break;
        }

        if (data.products && data.products.length > 0) {
          allProducts = allProducts.concat(data.products);
        }

        totalPages = data.pagination ? data.pagination.total_pages : 1;
        page++;
      }

      _cachedProducts = allProducts;
      return allProducts;
    })();

    return _fetchPromise;
  }

  /**
   * Render color swatches into a container element.
   * One swatch per product — products are already filtered to the same color group.
   */
  function renderSwatches(container, products, currentProductId) {
    let html = '';

    products.forEach(function (product) {
      const isChecked = String(product.id) === String(currentProductId);
      const inputId = 'color-group-swatch-' + product.id;
      const productUrl = product.url;
      const imageUrl = product.featured_image || '';
      const label = product.title;

      html += '<span class="swatch__button" data-tooltip="' + escapeHtml(label) + '">';
      html += '<input type="radio" name="color-group-swatches"';
      html += ' value="' + escapeHtml(String(product.id)) + '"';
      html += ' id="' + escapeHtml(inputId) + '"';
      if (isChecked) html += ' checked';
      html += ' onclick="window.location.href=\'' + escapeHtml(productUrl) + '\'"';
      html += '>';
      html += '<label for="' + escapeHtml(inputId) + '"';
      html += ' class="color-swatch-sister focus-inset" title="' + escapeHtml(label) + '">';
      if (imageUrl) {
        html += '<span class="swatch__image" style="background-image: url(\'' + escapeHtml(imageUrl) + '\')"></span>';
      }
      html += '<span class="visually-hidden">' + escapeHtml(label) + '</span>';
      html += '</label>';
      html += '</span>';
    });

    if (html) {
      container.innerHTML = html;
    }
    container.classList.remove('color-group-loading');
  }

  /**
   * Simple HTML escaping
   */
  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str)));
    return div.innerHTML;
  }

  /**
   * Initialize all color group swatch containers on the page
   */
  function init() {
    const containers = document.querySelectorAll('[data-color-group-swatches]');
    if (containers.length === 0) return;

    // All containers share the same collection, so fetch once
    const firstContainer = containers[0];
    const collectionHandle = firstContainer.getAttribute('data-collection-handle');

    if (!collectionHandle) {
      console.warn('Color group swatches: No collection handle specified');
      return;
    }

    fetchAllColorGroupProducts(collectionHandle)
      .then(function (allProducts) {
        containers.forEach(function (container) {
          const colorGroupValue = container.getAttribute('data-color-group-value');
          const currentProductId = container.getAttribute('data-current-product-id');

          // Filter products matching this color group
          const matchingProducts = allProducts.filter(function (p) {
            return p.color_group === colorGroupValue;
          });

          if (matchingProducts.length > 0) {
            renderSwatches(container, matchingProducts, currentProductId);
          } else {
            container.classList.remove('color-group-loading');
          }
        });
      })
      .catch(function (err) {
        console.error('Color group swatches: Failed to load', err);
        containers.forEach(function (c) { c.classList.remove('color-group-loading'); });
      });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
